import { useEffect, useRef, useState, type ReactNode } from 'react'

import {
  bookingPath,
  fetchBookingPage,
  type BookingPage,
} from '../../api/transport'
import { PageBand, PageShell } from '../../components/PageShell'
import {
  AddToCalendar,
  BookAgain,
  BookingFooter,
  ChangeOrCancel,
  ReceiptLink,
} from './BookingParts'
import { BookingTimeline } from './BookingTimeline'
import { whenLabel } from './bookingWords'
import { useFocusOnMount } from './focus'
import { ProgressBar } from './ProgressBar'
import { TransportHeader } from './TransportHeader'

/**
 * Screen 6, the confirmation (M40-T09, spec §C.3.6). Shown only once payment
 * has confirmed the booking (Decision 1): nothing here says "pending",
 * "provisional" or "awaiting approval".
 *
 * The private link carries the manage key in its fragment (README): the
 * address bar is set to it with `history.replaceState`, so a refresh opens
 * the booking page (T10) rather than Screen 1, and the key never reaches a
 * server in a URL. The trip's details come from the booking page's answer
 * (T08), the same one the booking page shows.
 */

/** "No 'by SMS' while SMS is off" (T09): SMS is not live before M41. */
const SMS_LIVE = false

export function ConfirmationScreen({
  reference,
  manageKey,
  onBookAgain,
}: {
  reference: string
  manageKey: string
  /** "Book the same trip again" (M40-T11): a new booking from Screen 1. */
  onBookAgain?: () => void
}): ReactNode {
  const heading = useFocusOnMount<HTMLHeadingElement>()
  const [page, setPage] = useState<BookingPage | null>(null)
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle')
  const linkInput = useRef<HTMLInputElement>(null)
  const path = bookingPath(reference, manageKey)
  const link = `${window.location.origin}${path}`

  useEffect(() => {
    window.history.replaceState(window.history.state, '', path)
  }, [path])

  useEffect(() => {
    const ctrl = new AbortController()
    fetchBookingPage(reference, manageKey, ctrl.signal).then(
      (p) => setPage(p),
      () => {
        // The confirmation stands without it: the link opens the full page.
      },
    )
    return () => ctrl.abort()
  }, [reference, manageKey])

  // No clipboard (an older browser, a page not served over https) or a
  // refusal: select the link and say so, rather than nothing happening.
  const copyFailed = () => {
    linkInput.current?.focus()
    linkInput.current?.select()
    setCopied('failed')
  }
  const copy = () => {
    setCopied('idle')
    if (!navigator.clipboard) {
      copyFailed()
      return
    }
    navigator.clipboard
      .writeText(link)
      .then(() => setCopied('copied'), copyFailed)
  }

  const by = page ? whenLabel(page.driver_details_by) : null

  return (
    <PageShell measure={640} as="main">
      <TransportHeader />
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        <h1
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          Booking confirmed — {reference}
        </h1>
        <ProgressBar current={5} />
        <p className="text-[14px] leading-relaxed text-ink-1">
          Your trip is booked and paid. We&apos;ll send the driver&apos;s name,
          phone number and vehicle plate
          {SMS_LIVE ? ' by SMS' : ''}
          {by ? ` by ${by}` : ' before your pickup'}.
        </p>

        <section
          aria-labelledby="save-link"
          className="flex flex-col gap-2 rounded-r3 border border-line-2 bg-bg-soft px-3 py-3"
        >
          <h2 id="save-link" className="text-[13px] font-semibold text-ink-1">
            Save this link
          </h2>
          <p className="text-[12.5px] text-ink-2">
            It opens your booking. Keep it private: anyone with the link can see
            your trip.
          </p>
          <input
            ref={linkInput}
            readOnly
            value={link}
            aria-label="Your private booking link"
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded-r3 border border-line-2 bg-bg-app px-2 py-2 font-mono text-[12px] text-ink-1"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={copy}
              className="inline-flex min-h-[44px] items-center rounded-r3 border border-line-2 bg-bg-app px-3 text-[13px] font-semibold text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              Copy link
            </button>
            <span role="status" className="text-[12.5px] text-ink-2">
              {copied === 'copied' && 'Copied'}
              {copied === 'failed' &&
                "Couldn't copy it here. The link is selected: copy it yourself."}
            </span>
          </div>
        </section>

        {page && (
          <>
            <section aria-labelledby="timeline" className="flex flex-col gap-2">
              <h2
                id="timeline"
                className="font-mono text-[10px] uppercase tracking-wide text-ink-3"
              >
                What happens next
              </h2>
              <BookingTimeline page={page} />
            </section>
            <div className="flex flex-wrap gap-2">
              <AddToCalendar page={page} />
              <ReceiptLink reference={reference} manageKey={manageKey} />
            </div>
          </>
        )}
        <ChangeOrCancel reference={reference} />
        {onBookAgain && <BookAgain onClick={onBookAgain} />}
        <BookingFooter />
      </PageBand>
    </PageShell>
  )
}
